import type {
  MiceCandidate,
  MiceChooseInput,
  MiceChosenFlight,
  MiceDirectionDecision,
  MiceEventWindowFlight,
  MiceMoney,
  MiceNeedView,
  MiceRecommendation,
} from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { Button } from '@web/ui/core/atoms';
import { useState } from 'react';
import { ColorDot } from './color_dot';
import { DirectionCards, suggestionFlights } from './direction_cards';
import { FlightOptionsPanel } from './flight_options_panel';
import { formatMoney, formatShortDay, sameFlight } from './need_format';

type Direction = 'OUTWARD' | 'RETURN';

/** Um sentido do detalhe: a decisão do agente (nula numa recomendação antiga) e o voo escolhido. */
export interface NeedLeg {
  direction: Direction;
  decision: MiceDirectionDecision | null;
  chosen: MiceChosenFlight | null;
}

/** O cartão marcado num sentido: um voo da versão vigente ou um da lista da faixa do evento. */
export interface CardPick {
  id: string;
  source: 'suggestion' | 'event_list';
}

/** O cartão marcado em cada sentido; vazio quando o sentido não tem voo escolhido. */
export type CardPicks = Partial<Record<Direction, CardPick>>;

/** O voo que o operador trouxe da lista da faixa do evento, por sentido. */
export type ListedFlights = Partial<Record<Direction, MiceEventWindowFlight>>;

const chosenOf = (rec: MiceRecommendation, direction: Direction): MiceChosenFlight | null =>
  direction === 'OUTWARD' ? rec.outbound : rec.inbound;

/** Os sentidos da versão: pelas decisões; sem elas (recomendação antiga), pelos voos escolhidos. */
export function legsOf(rec: MiceRecommendation): NeedLeg[] {
  if (rec.directions.length > 0) {
    return rec.directions.map((decision) => ({
      direction: decision.direction,
      decision,
      chosen: chosenOf(rec, decision.direction),
    }));
  }
  return (['OUTWARD', 'RETURN'] as const).flatMap((direction) => {
    const chosen = chosenOf(rec, direction);
    return chosen ? [{ direction, decision: null, chosen }] : [];
  });
}

/** O que vem marcado: a escolha do agente em cada sentido. */
export const agentPicks = (legs: NeedLeg[]): CardPicks =>
  Object.fromEntries(
    legs.flatMap((leg) =>
      leg.chosen ? [[leg.direction, { id: leg.chosen.id, source: 'suggestion' }]] : [],
    ),
  );

const isAgentPick = (leg: NeedLeg, pick: CardPick): boolean =>
  pick.source === 'suggestion' && pick.id === leg.chosen?.id;

/** Os sentidos em que o operador marcou outro cartão que não o do agente. */
function changesOf(legs: NeedLeg[], picks: CardPicks): MiceChooseInput['choices'] {
  return legs.flatMap((leg) => {
    const pick = picks[leg.direction];
    if (!pick || isAgentPick(leg, pick)) return [];
    const { direction } = leg;
    return pick.source === 'event_list'
      ? [{ direction, candidateId: pick.id, source: 'event_list' as const }]
      : [{ direction, candidateId: pick.id }];
  });
}

/** O preço do cartão marcado: o voo cotado tem o da tarifa; os da busca e da lista, o da busca. */
function pickedPrice(
  leg: NeedLeg,
  pick: CardPick | undefined,
  listed: ListedFlights,
): MiceMoney | null {
  if (!pick) return null;
  if (pick.source === 'event_list') return listed[leg.direction]?.price ?? null;
  const id = pick.id;
  if (leg.chosen?.id === id) return leg.chosen.farePrice;
  const options: (MiceCandidate | null | undefined)[] = [
    leg.decision?.requested,
    leg.decision?.eventWindowOption,
  ];
  return options.find((f) => f?.id === id)?.price ?? null;
}

function totalOf(legs: NeedLeg[], picks: CardPicks, listed: ListedFlights): MiceMoney | null {
  const prices = legs.flatMap((leg) => pickedPrice(leg, picks[leg.direction], listed) ?? []);
  const first = prices[0];
  if (!first) return null;
  return { amount: prices.reduce((sum, p) => sum + p.amount, 0), currency: first.currency };
}

const LEG_LABEL = { OUTWARD: 'mice.table.outbound', RETURN: 'mice.table.inbound' } as const;

/** O que o operador pode fazer com os cartões: marcar um, ou trazer um voo da lista. */
export interface LegChoice {
  picks: CardPicks;
  listed: ListedFlights;
  onPick: (direction: Direction, pick: CardPick) => void;
  /** Um voo da lista que não tem cartão no sentido: vira o cartão "Escolha da operação". */
  onList: (direction: Direction, flight: MiceEventWindowFlight) => void;
  readOnly: boolean;
}

/** Um grupo por sentido ("Ida · 12/11" e a cor), com os cartões lado a lado e a lista da faixa do
 *  evento, aberta num sentido por vez. */
export function NeedLegs({
  need,
  projectId,
  legs,
  choice,
}: Readonly<{ need: MiceNeedView; projectId: string; legs: NeedLeg[]; choice: LegChoice }>) {
  const [open, setOpen] = useState<Direction | null>(null);
  const version = need.recommendation?.version ?? 0;
  const use = (leg: NeedLeg, flight: MiceEventWindowFlight) => {
    const existing = suggestionFlights(leg.decision, leg.chosen).find((f) => sameFlight(f, flight));
    if (existing) choice.onPick(leg.direction, { id: existing.id, source: 'suggestion' });
    else choice.onList(leg.direction, flight);
    setOpen(null);
  };
  return (
    <>
      {legs.map((leg) => {
        const date = leg.direction === 'OUTWARD' ? need.outboundDate : need.returnDate;
        const title = [t(LEG_LABEL[leg.direction]), date ? formatShortDay(date) : null]
          .filter(Boolean)
          .join(' · ');
        return (
          <fieldset key={leg.direction} className="flex min-w-0 flex-col gap-2">
            <legend className="mb-2 flex items-center gap-2 font-mono text-tiny font-medium uppercase tracking-eyebrow-wide text-faint-foreground">
              {title}
              <ColorDot color={leg.decision?.color ?? null} />
            </legend>
            <DirectionCards
              decision={leg.decision}
              chosen={leg.chosen}
              listed={choice.listed[leg.direction] ?? null}
              selected={choice.picks[leg.direction]}
              onSelect={(pick) => {
                choice.onPick(leg.direction, pick);
              }}
              readOnly={choice.readOnly}
            />
            {!choice.readOnly &&
              version !== 0 &&
              (open === leg.direction ? (
                <FlightOptionsPanel
                  projectId={projectId}
                  needId={need.id}
                  version={version}
                  direction={leg.direction}
                  title={title}
                  agent={leg.chosen}
                  onUse={(flight) => {
                    use(leg, flight);
                  }}
                  onClose={() => {
                    setOpen(null);
                  }}
                />
              ) : (
                <div className="self-start">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setOpen(leg.direction);
                    }}
                  >
                    {t('mice.flightOptions.open')}
                  </Button>
                </div>
              ))}
          </fieldset>
        );
      })}
    </>
  );
}

/**
 * O total da escolha na tela e "Marcar para emitir": com os cartões do agente, aprova; com outro
 * cartão em algum sentido, um só `choose` com todos os sentidos trocados, que já aprova depois de
 * cotar.
 */
export function MarkFooter({
  need,
  rec,
  legs,
  picks,
  listed,
  choosing,
  onApprove,
  onChoose,
}: Readonly<{
  need: MiceNeedView;
  rec: MiceRecommendation;
  legs: NeedLeg[];
  picks: CardPicks;
  listed: ListedFlights;
  choosing: boolean;
  onApprove: (needId: string) => void;
  onChoose: (needId: string, choice: MiceChooseInput) => void;
}>) {
  const choices = changesOf(legs, picks);
  const total = totalOf(legs, picks, listed);
  // Já aprovada e sem troca, não há o que marcar: o botão some. Nos outros status sem troca, só a
  // `recommended` tem o que aprovar (o servidor só aprova essa).
  const hidden = choices.length === 0 && need.status === 'approved';
  const disabled = choosing || (choices.length === 0 && need.status !== 'recommended');
  return (
    <div className="flex flex-shrink-0 items-center justify-end gap-3 border-t border-border-soft px-5 py-3">
      {total ? (
        <span className="text-meta font-medium tabular-nums text-foreground">
          {t('mice.needs.total', { amount: formatMoney(total) })}
        </span>
      ) : null}
      {hidden ? null : (
        <Button
          disabled={disabled}
          onClick={() => {
            if (choices.length > 0) {
              onChoose(need.id, { choices, version: rec.version, approve: true });
            } else {
              onApprove(need.id);
            }
          }}
        >
          {t('mice.drawer.mark')}
        </Button>
      )}
    </div>
  );
}
